import { useState, useEffect } from "react";
import { Tabs, Tab, Box, Card, Button, Stack } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";

import { Vehiculo } from "src/models/types";
import { vehiculoColumns } from "src/api/vehiculoColumns";
import { getVehiculos, updateVehiculo } from "src/api/vehiculoService";
import { useNotifier } from "src/contexts/NotifierContext";

import { useAuth } from "../../../contexts/AuthContext"; 

import "../../../styles/HighlightChanges.css"; // CSS para resaltar cambios

export default function VehiculosBulkEdit() {
  const { user } = useAuth();
  const usuarioId = user?.idUsuario;
  const puedeEditar = user?.membresia !== 0;
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState<Vehiculo[]>([]);
  const [originalRows, setOriginalRows] = useState<Vehiculo[]>([]);
  const [cambios, setCambios] = useState<Vehiculo[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedVehiculo, setSelectedVehiculo] = useState<Vehiculo | null>(null);

  const { showSuccess, showError } = useNotifier();

  // Cargar vehículos desde API
  const loadVehiculos = async () => {
    setLoading(true);
    try {
      const data = await getVehiculos();
      setRows(data);
      setOriginalRows(data);
      setCambios([]);

      if (data.length > 0) {
        setSelectedVehiculo(data[0]); // selecciona la primera fila
      }
    } catch (error) {
      console.error(error);
      showError("Error al cargar los vehículos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVehiculos();
  }, []);

  // Cambio de pestaña
  const handleTabChange = (_: any, newValue: number) => setTab(newValue);

  // Actualiza cambios en DataGrid
  const handleCellEditCommit = (params: any) => {
    const updatedRow = {
      ...rows.find((r) => r.id === params.id),
      [params.field]: params.value,
    };

    setRows((prev) =>
      prev.map((r) => (r.id === updatedRow.id ? updatedRow : r))
    );

    setCambios((prev) => {
      const exists = prev.find((v) => v.id === updatedRow.id);
      return exists
        ? prev.map((v) => (v.id === updatedRow.id ? updatedRow : v))
        : [...prev, updatedRow];
    });

    // Actualiza el vehículo seleccionado si es el mismo
    if (selectedVehiculo?.id === updatedRow.id) {
      setSelectedVehiculo(updatedRow);
    }
  };

  // Guardar cambios fila por fila
  const handleGuardar = async () => {
    if (cambios.length === 0) return;
    setLoading(true);

    try {
      
      for (const vehiculo of cambios) {
        const vehiculoConUsuario = { ...vehiculo, usuario: { id: usuarioId } };
        console.log(
          "Enviando al backend:",
          JSON.stringify(vehiculoConUsuario, null, 2)
        );
        await updateVehiculo(vehiculoConUsuario);
      }

      showSuccess("Vehículos actualizados con éxito ✅");
      await loadVehiculos(); // recarga filas originales y limpia cambios
    } catch (error) {
      console.error("Error al actualizar los vehículos:", error);
      showError("Error al actualizar los vehículos ❌");
    } finally {
      setLoading(false);
    }
  };

  // Deshacer cambios
  const handleDeshacer = async () => {
    await loadVehiculos();
  };

  // Columnas con resaltado, tooltip y icono
  const columnsWithIcon: GridColDef[] = (vehiculoColumns[tab] as GridColDef[]).map(
    (col) => ({
      ...col,
      renderCell: (params) => {
        const originalRow = originalRows.find((r) => r.id === params.id);
        const changed = originalRow && params.value !== originalRow[params.field];

        return (
          <span
            title={changed ? `Original: ${originalRow[params.field]}` : ""}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            {params.value}
            {changed && <EditIcon fontSize="small" color="action" />}
          </span>
        );
      },
      cellClassName: (params) => {
        const originalRow = originalRows.find((r) => r.id === params.id);
        return originalRow && params.value !== originalRow[params.field]
          ? "highlighted-change"
          : "";
      },
    })
  );

  return (
    <Card style={{ padding: 16 }}>
      {/* Botones y placa arriba */}
      <Stack direction="row" spacing={2} mb={2} justifyContent="space-between">
        <Box>
          {selectedVehiculo && (
            <Box p={1} bgcolor="#f5f5f5" borderRadius={1}>
              <strong>Placa:</strong> {selectedVehiculo.placa} |{" "}
              <strong>Marca:</strong> {selectedVehiculo.marca} |{" "}
              <strong>Modelo:</strong> {selectedVehiculo.modelo}
            </Box>
          )}
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            disabled={cambios.length === 0}
            onClick={handleDeshacer}
          >
            Deshacer
          </Button>
          <Button
            variant="contained"
            disabled={!puedeEditar || cambios.length === 0}
            onClick={handleGuardar}
          >
            Guardar
          </Button>
          {!puedeEditar && (
          <Box
            p={1}
            mb={2}
            bgcolor="#fff3cd"
            color="#856404"
            border="1px solid #ffeeba"
            borderRadius={1}
          >
            Modo solo lectura: solo usuarios con membresía activa pueden modificar los vehículos.
          </Box>
        )}
        </Stack>
      </Stack>

      <Tabs
        value={tab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Datos Generales" />
        <Tab label="Líquidos" />
        <Tab label="Frenos" />
        <Tab label="Ruedas/Rodamientos" />
        <Tab label="Motor" />
        <Tab label="Varios" />
      </Tabs>

      <Box mt={2} style={{ height: 500 }}>
        <DataGrid
          rows={rows}
          columns={columnsWithIcon}
          editMode="cell"
          isCellEditable={() => puedeEditar}
          onCellEditCommit={puedeEditar ? handleCellEditCommit : undefined}
          onRowClick={(params) => setSelectedVehiculo(params.row)}
          autoHeight
          disableSelectionOnClick
          loading={loading}
        />
      </Box>
    </Card>
  );
}
